/* ---------------------------------------------------------------------------
   The bridge on the record: the stored mesh, rendered. Nothing rebuilt.

   WHAT THIS REPLACES, AND WHY IT IS A DIFFERENT KIND OF THING. The record used to
   show its 3D model by framing the vendored modeller (./viewport.ts) — a 1.6 MB
   application, a parametric engine and a WebGL context, booted on every visit to
   draw a bridge that had already been drawn. That is not a heavy way of rendering a
   mesh; it is not rendering a mesh at all. It is asking a bridge-design application
   to reconstruct the model from its parameters and then photographing the result,
   and everything that ever went wrong with an imported model went wrong in that gap:

     - The frame's picture depended on the DEVICE. An imported model's triangles
       lived in the importing browser's IndexedDB, so the same model drew correctly
       on one laptop and wrongly on another, or on the same laptop at a different
       port, because IndexedDB is keyed by origin.
     - Missing triangles did not produce an empty view, they produced ANOTHER
       BRIDGE. `App.LoadImportedCloudMesh` falls back to the parametric model the
       imported parameters describe — plausible, wrong, and it looks like the real
       thing to anybody who was not standing at the abutment.
     - And the frame WROTE. It is a modeller, so it autosaves; a frame that had
       fallen back saved the fallback, which cleared the model's imported flag on
       the server and put every other device on the same wrong bridge.

   So the record now draws the `.glb` the server derived from the CSiBridge export
   (functions/lib/meshGlb.ts). It is the source's own triangles, it is the same file
   the Site view and the Defect Inspector already load, it is identical on every
   device, and NOTHING HERE CAN REBUILD A BRIDGE — there is no parametric engine in
   this module to fall back to and no writer of any kind. A file that will not load
   is reported as a file that will not load.

   THE COMPONENT TREE COMES OUT OF THE SAME FILE. The record's Defects card and the
   "Log defect" picker need the model's component names, and they used to ask the
   framed modeller for them over postMessage — the last thing the frame was needed
   for. The derived geometry carries the hierarchy as glTF nodes (Imported Model >
   PierBent (36) > PierBent 1) with each label in `extras`, so the tree is read off
   the scene that is already being loaded to draw. One request, one artefact, one
   answer, and no frame.

   IT OWNS A CANVAS OUTSIDE THE RENDER ROOT, for the same reason ./viewport.ts owned
   an iframe there: a WebGL context cannot survive being moved by a reconciler, and
   ./dom.ts makes no promise that a given node survives a structural change above it.
   So the canvas is parked on document.body and followed over the placeholder the
   record leaves for it — `cropTo` and the placeholder class are shared with
   ./viewport.ts rather than reimplemented, because the arithmetic is the same and it
   is already under test.

   AND IT IS WHERE A DEFECT IS LOGGED. The toolbar's "Log defect" borrows the next
   click on the model and opens the register's own dialog at the point clicked —
   component named by the shared rule (`qualifiedNameAt`), point and normal on the
   draft — and every defect on the structure that has a point is drawn back onto
   the model as a pin, in the Defect Inspector's colours (./pins.ts). Recording is
   still the dialog's and the store's: this module opens one and writes nothing.

   THREE.JS IS LOADED ON DEMAND, as in src/map/scene3d.ts: it is ~600 kB against a
   register bundle of 90 kB, and a session that never opens a record with a model
   should not pay for it.
   --------------------------------------------------------------------------- */

import { actions, can, whyNot, type State } from '../core/store.ts';
import { componentNodeAt, componentTreeOf, nodeId, qualifiedNameAt } from '../data/componentTree.ts';
import { playedEntries } from '../data/buildSequence.ts';
import { defectCodeLabel, isOpenStatus } from '../data/defects.ts';
import type { ProgressEntry, RegisterDefect, SiteReturn, Vec3 } from '../data/types.ts';
import type { Baseline } from '../data/variance.ts';
import { loadGeometryBytes, parseGlb } from '../core/geometryCache.ts';
import { findBridge, heroModel } from '../data/derive.ts';
import { buildGround, type Ground } from '../map/ground3d.ts';
import { isolateRadiusKm, setIsolateRadiusKm, type IsolateRadiusKm } from '../map/isolate.ts';
import { appForShell } from '../core/apps.ts';
import { MODEL_FOV, frameModel, modelBounds, visibleBounds } from './framing.ts';
import { PLACEHOLDER, cropTo, type Sides } from './viewport.ts';
import { PIN_RADIUS, severityRamp } from './pins.ts';
import {
  TOOLBAR_H,
  axialDistance,
  axialFrame,
  mountViewToolbar,
  syncOrtho,
  type DefectCommands,
  type SimCommands,
  type StandardView,
  type TerrainCommands,
  type ViewCommands,
  type ViewToolbar,
} from './meshTools.ts';
import { startBuildSim, type BuildSim, type SimInput } from './buildSim.ts';

/* --------------------------- what is on screen --------------------------- */

/** The canvas's home — a sibling of the render root, not inside it. */
let host: HTMLDivElement | null = null;
/** Tears down the live scene: context, geometries, materials, render loop. */
let dispose: (() => void) | null = null;
/** Cancels the loop that keeps `host` over the placeholder. */
let stopTracking: (() => void) | null = null;
/**
 * What to tell the renderer when the placeholder changes size.
 *
 * A mutable hook rather than a second tracker, because the follow loop starts
 * BEFORE the scene does — three.js is a 600 kB on-demand chunk, and an empty host
 * parked in the corner of the window until it arrives is worse than no host at all.
 * So one loop runs for the life of the mount and this is filled in when there is
 * something to resize.
 */
let onResize: ((w: number, h: number) => void) | null = null;
/**
 * Repaint the backdrop for a theme, once there is a scene to repaint.
 *
 * The framed modeller could only be told its theme once, on its own URL at boot, so
 * a theme toggle cost the user their camera position — the whole frame had to be
 * rebuilt. Here the theme decides one colour on a live scene, so it is a hook rather
 * than part of the mount key: toggling the theme while orbiting a bridge leaves the
 * bridge exactly where it was.
 */
let onTheme: ((theme: string) => void) | null = null;
/**
 * Start and stop the render loop as the placeholder comes and goes.
 *
 * A hook for the same reason `onResize` is one: the follow loop starts before the
 * scene does, so what it has to say about visibility has to reach whatever is there
 * to hear it. `onScreen` is the answer it left last, for a scene that arrives after
 * the panel has already been scrolled past.
 */
let onVisible: ((drawing: boolean) => void) | null = null;
let onScreen = true;

/**
 * Whether a plain wheel zooms the model, or is left to scroll the page.
 *
 * ON THE DESK IT ZOOMS. The record's canvas fills a panel of its own that has
 * nothing else in it to scroll (see detailScreen in src/screens/detail.ts), so the
 * reason the wheel was taken off the camera — a canvas inside a scrolling column,
 * flying out to maxDistance while somebody tried to scroll past it — no longer
 * applies there, and the wheel does what it does in the modeller.
 *
 * ON THE FIELD RECORD IT SCROLLS, as it always has: that record is one scrolling
 * page with the model in a strip of it, and ctrl/⌘+wheel (a trackpad pinch) zooms.
 *
 * A hook as well as a value, because the shell is read on every paint and the
 * camera may already be up when it changes.
 */
let wheelZooms = false;
let onWheelMode: ((zooms: boolean) => void) | null = null;

/**
 * What the live canvas is showing.
 *
 * The theme is deliberately NOT part of this. It is not a property of what is on
 * screen, only of how it is lit, and `onTheme` applies it without a remount — see
 * there for why the framed modeller could not do the same.
 *
 * The geometry version IS part of it. An import replaces the `.glb` under the same
 * model id, and the record remounts from the import's progress slot before the
 * index carrying the new version has landed — so without it the canvas stays on
 * the bytes the old version vouched for.
 */
let showing: { bridgeId: string; modelId: string; version: string | null } | null = null;
/** The theme the backdrop is currently painted for. */
let painted: string | null = null;

/** The generation of the current mount, so a load that lands late can be dropped. */
let generation = 0;

/**
 * The canvas toolbar (./meshTools.ts), mounted with the host so it is there from
 * the first frame, and the camera it drives once `build` has one.
 *
 * The toolbar is handed `delegate` rather than the live commands because it is
 * built before three.js has arrived; until `camera` is filled in, every button
 * reports "not ready" and does nothing.
 */
let toolbar: ViewToolbar | null = null;
let camera: ViewCommands | null = null;

const delegate: ViewCommands = {
  zoomIn: () => camera?.zoomIn(),
  zoomOut: () => camera?.zoomOut(),
  zoomAll: () => camera?.zoomAll(),
  standardView: (v) => camera?.standardView(v),
  activeView: () => camera?.activeView() ?? null,
  projection: () => camera?.projection() ?? 'perspective',
  setProjection: (m) => camera?.setProjection(m),
  armOrbitPoint: (on) => camera?.armOrbitPoint(on),
  orbitPointArmed: () => camera?.orbitPointArmed() ?? false,
  ready: () => camera?.ready() ?? false,
};

/* --------------------------- defects on the model --------------------------- */

/**
 * What the pins should show: the structure's placed defects, and the one being
 * logged right now if it was placed on the model.
 *
 * Held here and pushed through `onPins` for the same reason the theme is: the
 * scene arrives after the state that describes it, and a defect logged while the
 * canvas is up must reach it without a remount.
 */
interface PinSet {
  defects: readonly RegisterDefect[];
  draft: Vec3 | null;
}
let pins: PinSet = { defects: [], draft: null };
const NO_PINS: PinSet = { defects: [], draft: null };
/** Whether the application on screen shows defects on the model — Bridge Inspection only. */
let defectsShown = true;
let onPins: ((next: PinSet) => void) | null = null;

/** Null while this person may log a defect; otherwise the reason, for the button's title. */
let refusal: string | null = null;
/** Which structure a placement is logged against. */
let placingFor: string | null = null;
/** The desk has a Defects panel to open when a pin is clicked; the field record does not. */
let pinOpensPanel = false;

/* The toolbar's Log defect, delegated for the reason `delegate` is: the bar is
   built before three.js has arrived. */
let place: DefectCommands | null = null;
const defectDelegate: DefectCommands = {
  armPlace: (on) => place?.armPlace(on),
  placeArmed: () => place?.placeArmed() ?? false,
  refusal: () => refusal,
};

/* --------------------------- the construction playback --------------------------- */

/**
 * What Simulate plays: the structure's works entries and daily returns, pushed
 * through `onSimInput` for the reason pins are — the scene arrives after the
 * state that describes it, and an entry recorded while the playback is open must
 * reach it without a remount. See ./buildSim.ts.
 */
const NO_SIM: SimInput = { entries: [], returns: [] };
let simInput: SimInput = NO_SIM;
/**
 * Whether the application on screen simulates: Construction Monitoring plays the
 * record, Construction Scheduler the accepted programme (`simInput.programme`).
 */
let simShown = false;
/** The Scheduler's ribbon asked for Simulate before the model was up: start it once it is. */
let simPending = false;
let onSimInput: ((next: SimInput) => void) | null = null;

/* Delegated for the reason `delegate` is: the bar is built before three.js. */
let simulate: SimCommands | null = null;
const simDelegate: SimCommands = {
  toggle: () => simulate?.toggle(),
  running: () => simulate?.running() ?? false,
  refusal: () => simulate?.refusal() ?? simRefusal(),
  idleTitle: () => simIdleTitle(),
};

function simIdleTitle(): string {
  return simInput.programme !== undefined
    ? 'Simulate the construction on the model, day by day, as the programme plans it'
    : 'Play the construction record back on the model, day by day';
}

/** Null where there is something to play, else why not. */
function simRefusal(): string | null {
  if (simInput.programme !== undefined) {
    return simInput.programme?.activities.some((a) => !a.isGroup)
      ? null
      : 'There is no accepted programme for this structure to simulate';
  }
  return simInput.entries.length ? null : 'No work has been recorded against this structure to play back';
}

/**
 * Start the playback from outside the canvas — Construction Scheduler's ribbon.
 * The model may still be loading (the ribbon has just opened it), so the wish is
 * kept and honoured the moment the structure is on screen. Already playing, it
 * is left playing.
 */
export function startSimulation(): void {
  if (simulate?.running()) return;
  simPending = true;
  runPendingSim();
}

function runPendingSim(): void {
  if (!simPending || !simulate || !simShown || simRefusal() !== null) return;
  if (simulate.running()) {
    simPending = false;
    return;
  }
  simulate.toggle();
  if (simulate.running()) simPending = false;
}

/* --------------------------- the ground --------------------------- */

/* The toolbar's Terrain, delegated for the reason `delegate` is. The radius is the
   device's isolate setting, shared with the base-map page (src/map/isolate.ts). */
let ground: TerrainCommands | null = null;
const terrainDelegate: TerrainCommands = {
  toggle: () => ground?.toggle(),
  shown: () => ground?.shown() ?? false,
  busy: () => ground?.busy() ?? false,
  radiusKm: () => ground?.radiusKm() ?? isolateRadiusKm(),
  setRadiusKm: (km) => ground?.setRadiusKm(km),
  refusal: () => ground?.refusal() ?? null,
};


/**
 * How far a pointer may travel, in CSS pixels, and still count as a click on the
 * model rather than the start of an orbit — the Defect Inspector's TAP_SLOP_PX,
 * and for its reason: while placing, a drag must still turn the model, or there
 * would be no way round to the far side of a pier to log what is on it.
 */
const CLICK_SLOP_PX = 10;

/** The draft's own marker: the register's accent, since it is not yet a finding. */
const DRAFT_COLOUR = { light: 0x416180, dark: 0x9ab8d6 };

/** One line naming a pin, for the canvas's tooltip. */
function pinLabel(d: RegisterDefect): string {
  return [
    `${d.severity} · ${defectCodeLabel(d.code)}`,
    d.component ?? 'No component',
    d.status === 'Open' ? null : d.status,
  ]
    .filter(Boolean)
    .join(' — ');
}

/**
 * How long the arms of the orbit-point cross are, in metres — the Inspector's
 * PIVOT_ARM, and for its reasons (src/inspector/scene.ts).
 */
const PIVOT_ARM = 1.2;

/* --------------------------- the component tree --------------------------- */

/*
   THE TREE IS READ, NOT BUILT HERE. `componentTreeOf` and the label rule it
   applies live in src/data/componentTree.ts beside the walks that consume the
   result, because the Defect Inspector's 3D view now names what a tap hit with
   the same rule (`qualifiedNameAt`) — and a second copy of it here is how a
   defect placed by tapping a pier ends up filed under a name the card cannot
   find. See the note at the foot of that file.
*/

/* --------------------------- placement --------------------------- */

/**
 * Follow the placeholder, cropped to as much of it as is on screen.
 *
 * Lifted in shape from ./viewport.ts's `track`, and for the same reasons stated
 * there: a fixed element on document.body is clipped by nothing, the record is a
 * scrolling pane, and the cheapest correct answer is to copy the placeholder's box
 * every frame. What differs is what happens on a resize — this owns the renderer,
 * so it can tell it the new size instead of letting a framed camera re-frame itself.
 */
function track(): void {
  let raf = 0;
  let lastW = 0;
  let lastH = 0;

  /* Off screen, in every sense that matters here: not laid out, not measurable, or
     cropped away to nothing. The render loop is told as well as the host, because a
     canvas nobody can see is not worth drawing — see `setDrawing` in `build`. */
  const hide = (): void => {
    if (host) host.style.visibility = 'hidden';
    onScreen = false;
    onVisible?.(false);
  };

  const step = (): void => {
    raf = requestAnimationFrame(step);
    if (!host) return;

    const slot = document.querySelector<HTMLElement>(`.${PLACEHOLDER}`);
    if (!slot) {
      hide();
      return;
    }
    const box = slot.getBoundingClientRect();
    if (box.width < 2 || box.height < 2) {
      hide();
      return;
    }

    let limit: Sides = {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
    for (let el = slot.parentElement; el; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (style.overflowX === 'visible' && style.overflowY === 'visible') continue;
      const r = el.getBoundingClientRect();
      limit = {
        top: Math.max(limit.top, r.top),
        left: Math.max(limit.left, r.left),
        right: Math.min(limit.right, r.right),
        bottom: Math.min(limit.bottom, r.bottom),
      };
    }

    const crop = cropTo(box, limit);
    if (crop.hidden) {
      hide();
      return;
    }

    host.style.visibility = 'visible';
    onScreen = true;
    onVisible?.(true);
    host.style.transform = `translate(${box.left}px, ${box.top}px)`;
    host.style.width = `${box.width}px`;
    host.style.height = `${box.height}px`;
    host.style.clipPath =
      crop.top || crop.right || crop.bottom || crop.left
        ? `inset(${crop.top}px ${crop.right}px ${crop.bottom}px ${crop.left}px)`
        : '';

    /* Rounded, and only on a real change: `getBoundingClientRect` returns
       fractional pixels that jitter during a scroll, and resizing a WebGL drawing
       buffer every frame reallocates it. */
    const w = Math.round(box.width);
    const h = Math.round(box.height);
    if (w !== lastW || h !== lastH) {
      lastW = w;
      lastH = h;
      onResize?.(w, h);
    }
  };

  raf = requestAnimationFrame(step);
  stopTracking = () => cancelAnimationFrame(raf);
}

/**
 * The nearest ancestor a wheel could actually scroll, or null if there is none.
 *
 * `overflow: hidden` does not count: it is scrollable programmatically but not by
 * the user, and forwarding a wheel into one would move a box the browser itself
 * would have left alone. The content has to overrun the box as well, or this would
 * claim a pane that has nothing to scroll and swallow the notch.
 */
function scrollerOf(from: HTMLElement | null): HTMLElement | null {
  for (let el = from; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll';
    if (scrolls && el.scrollHeight > el.clientHeight + 1) return el;
  }
  return null;
}

/** A wheel's delta in pixels, whichever unit the mouse reported it in. */
function wheelPixels(event: WheelEvent, page: number): number {
  if (event.deltaMode === 1) return event.deltaY * 16; // lines, at a line's height
  if (event.deltaMode === 2) return event.deltaY * page; // pages
  return event.deltaY;
}

/* --------------------------- the scene --------------------------- */

/** Background behind the model, per theme. Matches the panel it sits in. */
const BACKDROP = { light: 0xf1f2f6, dark: 0x14161c };

/**
 * Build the scene and start drawing. Resolves when the file is on screen.
 *
 * Every failure resolves too, having said so through the store: geometry that will
 * not load is a fact about the record worth stating, and there is no fallback
 * picture this module is willing to invent.
 */
async function build(
  bridgeId: string,
  modelId: string,
  theme: string,
  mine: number,
  /* The index's fingerprint of the exported bytes. With it the cache settles
     freshness against what it holds instead of asking — see loadGeometryBytes. */
  version: string | null,
): Promise<void> {
  /* THE FILE AND THE LIBRARY AT THE SAME TIME, and they used to be one after the
     other. The three dynamic imports below are ~600 kB of three.js and
     camera-controls, and the `.glb` was not asked for until all three had landed and
     a whole scene had been built around them — two waits end to end, on a connection
     where the first is the slower. They need nothing from each other until the
     moment of the parse, so both start here and the parse joins them.

     Costs nothing when the geometry is already on the device: src/core/geometryCache.ts
     answers out of IndexedDB in a disk read, and this promise is simply resolved by
     the time anything awaits it.

     `catch` ON THE WAY PAST, not instead of the await below. A download that fails
     while the library is still arriving would otherwise be an unhandled rejection in
     the console — the await inside the try block is still what reports it, through
     the store, in the form the record can draw. */
  let onFresh: ((bytes: ArrayBuffer) => void) | null = null;
  const loading = loadGeometryBytes(
    bridgeId,
    modelId,
    (fresh) => {
      if (mine !== generation) return; // unmounted while the check was in flight
      onFresh?.(fresh.bytes);
    },
    version,
  );
  loading.catch(() => undefined);

  const [THREE, controls, loaders] = await Promise.all([
    import('three'),
    import('camera-controls'),
    import('three/examples/jsm/loaders/GLTFLoader.js'),
  ]);
  if (mine !== generation || !host) return;

  const CameraControls = controls.default;
  CameraControls.install({
    THREE: THREE as unknown as Parameters<typeof CameraControls.install>[0]['THREE'],
  });

  /* MEASURED OFF THE PLACEHOLDER, NOT THE HOST, because at this moment the host
     may have no box at all. `track()` is what gives the host its width and height
     and it does that on a requestAnimationFrame; this function reaches here after
     awaiting three dynamic imports, which on a warm cache resolve in a microtask —
     BEFORE that first frame. The host is then 0 × 0, the `|| 900` fallback below
     fires, and the renderer is built at a made-up 900 × 420 with a made-up aspect.
     The placeholder is ordinary laid-out DOM and always has its real box. */
  const slot = document.querySelector<HTMLElement>(`.${PLACEHOLDER}`);
  const box = (slot ?? host).getBoundingClientRect();
  const width = Math.max(1, Math.round(box.width) || 900);
  /* Less the toolbar docked across the top of the host — see ./meshTools.ts. */
  const height = Math.max(1, (Math.round(box.height) || 420) - TOOLBAR_H);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(width, height, false);
  renderer.domElement.className = 'det-live__canvas';
  host.appendChild(renderer.domElement);
  /* The pins' callouts, in the DOM over the canvas rather than in the scene, so
     the type is crisp text at one size however far away the bridge is. */
  const tagLayer = document.createElement('div');
  tagLayer.className = 'mpins';
  host.appendChild(tagLayer);

  const scene = new THREE.Scene();
  const backdrop = new THREE.Color();
  scene.background = backdrop;
  /* The construction playback, while it is open. Declared up here because the
     theme hook below recolours it. */
  let sim: BuildSim | null = null;
  /* The ground's recolour, while it is drawn — see `showGround` below. */
  let groundTheme: ((next: string) => void) | null = null;

  onTheme = (next) => {
    backdrop.setHex(next === 'dark' ? BACKDROP.dark : BACKDROP.light);
    painted = next;
    groundTheme?.(next);
    /* The severity ramp is per ground, so a theme change recolours the pins too. */
    onPins?.(pins);
    sim?.setTheme(next);
  };
  onTheme(theme);

  /* Three lights and no shadow map. A shadow map on a 155-part mesh costs a second
     render pass to show the bridge casting a shadow on nothing — there is no ground
     in this view, that is the Site view's job. What the model needs is enough
     directional variation to read its own form: a key light high and to one side, a
     fill from the opposite side at a third of the strength so the shaded faces are
     not black, and a hemisphere term so the undersides of the deck are legible,
     which is where an inspector looks. */
  const key = new THREE.DirectionalLight(0xffffff, 2.1);
  key.position.set(0.6, 1, 0.45);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.7);
  fill.position.set(-0.5, 0.35, -0.6);
  scene.add(fill);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x6b7280, 1.05));

  /* MODEL_FOV, shared with the Defect Inspector and the works entry view so the same
     `.glb` reads as the same photograph in all three. See framing.ts. */
  const lens = new THREE.PerspectiveCamera(MODEL_FOV, width / height, 0.5, 20_000);
  /* The modeller's arrangement: the controls stay bound to the perspective camera
     in both projections and the orthographic one is derived from it every frame
     (`syncOrtho`), so every gesture means the same thing in both. */
  const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100_000);
  const orbit = new CameraControls(lens, renderer.domElement);
  // A model is a thing on a table, not a place to walk around in: dolly to a point
  // rather than truck, and never let the camera through the far side of the deck.
  orbit.dollyToCursor = true;

  /* ON THE FIELD RECORD THE WHEEL BELONGS TO THE PAGE, NOT TO THE CAMERA (see
     `wheelZooms` for the desk) — and everywhere it used to be the
     camera's, which is what made this panel look as though it kept unloading itself.

     camera-controls binds the wheel to a dolly by default and calls
     `preventDefault()` on it (see `onMouseWheel` in camera-controls.module.js). This
     canvas is `position: fixed` over a placeholder inside a scrolling panel, so a
     wheel notch with the pointer over the bridge never reached that panel: the record
     did not move, and the camera flew out towards `maxDistance` instead — ten times
     the model's own radius. The bridge becomes a dot and then nothing, leaving the
     scene's backdrop (BACKDROP.light, a panel-coloured grey) filling the box, and
     scrolling "back" dollies it in again over as many notches as it took to leave.
     Nothing is torn down and nothing reloads, which is why the progress sweep never
     reappears and no state change can be found to explain it.

     ZOOM IS STILL HERE, on ctrl/⌘+wheel — which is also what a trackpad pinch
     arrives as, so the gesture somebody means by "zoom this" works and the one they
     mean by "scroll the record" is left to the record. Drag still orbits: a drag is
     unambiguous over a 3D view, and it is not how anybody scrolls a page. */
  /* The modeller's wheel, where the wheel zooms: camera-controls' own dolly, toward
     the pointer (`dollyToCursor` above) at Canvas3D's `dollySpeed` — the same hand
     as the Defect Inspector, which sets both the same way. */
  orbit.dollySpeed = 0.5;
  onWheelMode = (zooms) => {
    orbit.mouseButtons.wheel = zooms ? CameraControls.ACTION.DOLLY : CameraControls.ACTION.NONE;
  };
  onWheelMode(wheelZooms);
  const onWheel = (event: WheelEvent): void => {
    /* camera-controls has the wheel, and has already taken it. */
    if (wheelZooms) return;
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      /* Proportional to where the camera already is, so a notch means the same
         thing close in and far out — the same feel as camera-controls' own
         0.95-per-notch factor, arrived at through the public API rather than its
         internals. */
      const notches = event.deltaMode === 1 ? event.deltaY / 3 : event.deltaY / 100;
      void orbit.dollyTo(orbit.distance * Math.pow(1.05, notches), true);
      return;
    }

    /* AND THE RECORD SCROLLS BY HAND, because the browser will not do it for us.
       Taking the dolly off the wheel is only half of it: scroll chaining walks the
       DOM ancestors of whatever is under the pointer, and this canvas is parked on
       document.body, not inside the panel it is drawn over. Its only ancestors are
       `body` and `html`, both `overflow: hidden`, so the wheel would have reached
       nothing at all and the 3D view would have become a dead patch the record
       cannot be scrolled through — a different fault with the same look.

       The placeholder is the element that IS in the panel, so the panel is found
       from there rather than from the canvas. */
    const scroller = scrollerOf(document.querySelector<HTMLElement>(`.${PLACEHOLDER}`));
    if (!scroller) return;
    event.preventDefault();
    scroller.scrollTop += wheelPixels(event, scroller.clientHeight);
  };
  renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

  /* WIRED BEFORE THE GEOMETRY IS ASKED FOR, and it used to be wired after. `track()`
     only calls this when the placeholder's size CHANGES, and it records the size it
     last reported — so the one report that happens while this is still null is lost
     for the life of the mount, and the renderer keeps whatever size `build` guessed
     while the canvas is stretched by CSS to the real box. Assigning it here means
     the first frame `track()` measures reaches a live renderer. */
  onResize = (w, h) => {
    const below = Math.max(1, h - TOOLBAR_H);
    renderer.setSize(w, below, false);
    lens.aspect = w / below;
    lens.updateProjectionMatrix();
  };

  let structure: import('three').Object3D | null = null;
  const clock = new THREE.Clock();
  let raf = 0;
  let drawing = false;

  /* THE TOOLBAR'S STATE. What projection is drawn, which of the six standard views
     the camera is sitting on (until a drag moves it off), whether the next click
     sets the orbit point, and the cross drawn at that point. */
  let projection: 'perspective' | 'orthographic' = 'perspective';
  let view: StandardView | null = null;
  let arming = false;
  let pivot: import('three').LineSegments | null = null;
  /* LOG DEFECT's state: whether the next click places one, the markers, and which
     defect each marker is. */
  let placing = false;
  const pinGroup = new THREE.Group();
  pinGroup.name = 'defect-pins';
  scene.add(pinGroup);
  const pinIds = new Map<string, RegisterDefect>();
  /* One callout per drawn defect: the label, the point it names, and whether the
     structure stands between that point and the camera. */
  interface Tag {
    el: HTMLDivElement;
    at: import('three').Vector3;
    hidden: boolean;
  }
  let tags: Tag[] = [];
  /* Occlusion is a raycast per pin against the whole model, so it is re-tested at
     most this often while the camera moves, and once more when it settles. */
  const OCCLUDE_MS = 120;
  let occludedAt = 0;
  let occludeDirty = true;

  const active = (): import('three').Camera => {
    if (projection === 'orthographic') {
      syncOrtho(ortho, lens, orbit.distance);
      return ortho;
    }
    return lens;
  };

  const draw = (): void => {
    raf = requestAnimationFrame(draw);
    const dt = clock.getDelta();
    const moved = orbit.update(dt);
    sim?.frame(dt);
    const cam = active();
    renderer.render(scene, cam);
    placeTags(cam, moved);
  };

  const probe = new THREE.Vector3();
  const probeNdc = new THREE.Vector2();
  const tagRay = new THREE.Raycaster();

  /* THE CALLOUTS FOLLOW THE CAMERA. Projected every frame, which is cheap; tested
     against the structure only on the throttle above, which is not. A pin the
     deck hides keeps its sphere hidden by the depth buffer, and its label goes
     with it rather than floating over the soffit naming something out of sight. */
  const placeTags = (cam: import('three').Camera, moved: boolean): void => {
    if (!tags.length) return;
    if (moved) occludeDirty = true;
    const now = performance.now();
    const occlude = occludeDirty && structure !== null && (!moved || now - occludedAt > OCCLUDE_MS);
    if (occlude) {
      occludedAt = now;
      if (!moved) occludeDirty = false;
    }
    const w = renderer.domElement.clientWidth;
    const h = renderer.domElement.clientHeight;
    for (const tag of tags) {
      probe.copy(tag.at).project(cam);
      const off = probe.z < -1 || probe.z > 1 || Math.abs(probe.x) > 1.05 || Math.abs(probe.y) > 1.05;
      if (occlude && !off && structure) {
        probeNdc.set(probe.x, probe.y);
        tagRay.setFromCamera(probeNdc, cam);
        const reach = tagRay.ray.origin.distanceTo(tag.at);
        const hit = tagRay.intersectObject(structure, true)[0];
        tag.hidden = hit !== undefined && hit.distance < reach - PIN_RADIUS * 1.5;
      }
      const gone = off || tag.hidden;
      /* Inherit rather than visible: a child set visible shows through a host
         hidden by `track`, so the tags were left over the table when the
         model was folded to its header. */
      tag.el.style.visibility = gone ? 'hidden' : 'inherit';
      if (gone) continue;
      const x = ((probe.x + 1) / 2) * w;
      const y = ((1 - probe.y) / 2) * h;
      tag.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    }
  };

  const hidePivot = (): void => {
    if (!pivot) return;
    scene.remove(pivot);
    pivot.geometry.dispose();
    (pivot.material as import('three').Material).dispose();
    pivot = null;
  };

  /* A cross at the orbit point, because setting one is otherwise invisible — the
     picture does not move. Drawn over everything, since the point is on a surface
     and half of any marker there would be inside it. */
  const showPivot = (x: number, y: number, z: number): void => {
    hidePivot();
    const a = PIVOT_ARM;
    const geom = new THREE.BufferGeometry().setAttribute(
      'position',
      new THREE.Float32BufferAttribute([-a, 0, 0, a, 0, 0, 0, -a, 0, 0, a, 0, 0, 0, -a, 0, 0, a], 3),
    );
    const mat = new THREE.LineBasicMaterial({
      color: painted === 'dark' ? 0xf0f1f2 : 0x1a1c1e,
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    });
    pivot = new THREE.LineSegments(geom, mat);
    pivot.position.set(x, y, z);
    pivot.renderOrder = 10;
    scene.add(pivot);
  };

  /* setOrbitPoint is a focal offset that nothing else clears, so every reframe
     clears it first or it is drawn from a camera standing off to one side — see
     clearPivot in src/inspector/scene.ts. */
  const clearPivot = (): void => {
    void orbit.setFocalOffset(0, 0, 0, false);
    hidePivot();
  };

  const snapTo = (next: StandardView): void => {
    if (!structure) return;
    const box = visibleBounds(THREE, structure);
    if (!box) return;
    const at = axialFrame(THREE, box, next);
    const distance = axialDistance(at.sx, at.sy, lens.fov, lens.aspect);
    orbit.maxDistance = Math.max(orbit.maxDistance, distance * 1.5);
    lens.far = Math.max(lens.far, distance * 4);
    lens.updateProjectionMatrix();
    view = next;
    projection = 'orthographic';
    clearPivot();
    void orbit.setLookAt(
      at.centre.x + at.eye.x * distance,
      at.centre.y + at.eye.y * distance,
      at.centre.z + at.eye.z * distance,
      at.centre.x,
      at.centre.y,
      at.centre.z,
      true,
    );
    toolbar?.refresh();
  };

  /* Zoom All: along the current axis when one is chosen, as Canvas3D.ZoomAll does,
     and otherwise the three-quarter view every record opens on. */
  const zoomAll = (): void => {
    if (!structure) return;
    if (view) {
      snapTo(view);
      return;
    }
    const sphere = modelBounds(THREE, structure);
    if (!sphere) return;
    clearPivot();
    frameModel(THREE, orbit, sphere, true);
    toolbar?.refresh();
  };

  /* Canvas3D.ZoomBy: a step toward the target, clamped to the dolly limits. */
  const zoomBy = (factor: number): void => {
    const next = THREE.MathUtils.clamp(orbit.distance * factor, orbit.minDistance, orbit.maxDistance);
    void orbit.dollyTo(next, true);
  };

  const setArming = (on: boolean): void => {
    arming = on;
    /* One borrowed click at a time: arming the orbit point stands placement down. */
    if (on) placing = false;
    toolbar?.refresh();
  };

  const setPlacing = (on: boolean): void => {
    placing = on && refusal === null && structure !== null;
    if (placing) arming = false;
    toolbar?.refresh();
  };

  place = {
    armPlace: setPlacing,
    placeArmed: () => placing,
    refusal: () => refusal,
  };

  /* SIMULATE: opened on the model that is on screen, closed before anything
     takes that model away (a newer export, the end of the mount), because the
     playback holds the parts' original materials and is what puts them back. */
  const closeSim = (): void => {
    if (!sim) return;
    sim.close();
    sim = null;
    toolbar?.refresh();
  };
  simulate = {
    toggle: () => {
      if (sim) {
        closeSim();
        return;
      }
      if (!structure || !host || !simShown || simRefusal() !== null) return;
      sim = startBuildSim(THREE, structure, host, simInput, painted ?? theme, closeSim);
      toolbar?.refresh();
    },
    running: () => sim !== null,
    refusal: simRefusal,
    idleTitle: simIdleTitle,
  };
  onSimInput = (next) => {
    if (!sim) return;
    if (!simShown) closeSim();
    else sim.setInput(next);
  };

  /* THE MARKERS, rebuilt whole on every change. A structure carries tens of
     defects at most, so this is cheaper than being clever about which moved.

     Only defects with a point are drawn, for the reason drawPins in
     src/inspector/scene.ts gives: a marker at a component's bounding-box centre
     is a place nobody looked. Those stay in the Defects panel. */
  const drawPins = (next: PinSet): void => {
    for (const child of [...pinGroup.children]) {
      const mesh = child as import('three').Mesh;
      mesh.geometry.dispose();
      (mesh.material as import('three').Material).dispose();
      pinGroup.remove(child);
    }
    pinIds.clear();
    tagLayer.replaceChildren();
    tags = [];
    occludeDirty = true;
    const light = painted !== 'dark';

    /* A stem up from the pin to a chip naming the defect type, in the pin's colour. */
    const tagFor = (
      d: RegisterDefect,
      colour: number,
      faded: boolean,
      at: import('three').Vector3,
    ): HTMLDivElement => {
      const el = document.createElement('div');
      el.className = faded ? 'mpin mpin--faded' : 'mpin';
      el.style.setProperty('--mpin', `#${colour.toString(16).padStart(6, '0')}`);
      el.style.visibility = 'hidden';
      const stem = document.createElement('span');
      stem.className = 'mpin__stem';
      const chip = document.createElement('span');
      chip.className = 'mpin__tag';
      chip.textContent = defectCodeLabel(d.code);
      el.append(stem, chip);
      tags.push({ el, at, hidden: false });
      return el;
    };

    const add = (
      at: Vec3,
      normal: Vec3 | null,
      color: number,
      faded: boolean,
    ): import('three').Mesh => {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(PIN_RADIUS, 12, 10),
        new THREE.MeshBasicMaterial({ color, transparent: faded, opacity: faded ? 0.35 : 1 }),
      );
      mesh.position.set(at.x, at.y, at.z);
      /* Nudged out along the normal so it sits on the surface, not half in it. */
      if (normal) {
        mesh.position.x += normal.x * PIN_RADIUS * 0.8;
        mesh.position.y += normal.y * PIN_RADIUS * 0.8;
        mesh.position.z += normal.z * PIN_RADIUS * 0.8;
      }
      pinGroup.add(mesh);
      return mesh;
    };

    for (const d of next.defects) {
      if (!d.pin) continue;
      /* Resolved defects are the structure's history: still on it, but receding. */
      const faded = !isOpenStatus(d.status);
      const colour = severityRamp(d.severity, light);
      const mesh = add(d.pin, d.normal, colour, faded);
      pinIds.set(mesh.uuid, d);
      tagLayer.appendChild(tagFor(d, colour, faded, mesh.position.clone()));
    }
    if (next.draft) {
      const mesh = add(next.draft, null, light ? DRAFT_COLOUR.light : DRAFT_COLOUR.dark, false);
      mesh.scale.setScalar(1.25);
    }
  };
  onPins = drawPins;

  /* THE GROUND: the land within the device's isolate radius, as a pale surface
     with its contour lines at their elevations, laid under the model in this
     canvas. See src/map/isolate.ts for the arithmetic and src/map/terrain.ts for
     what the heights are relative to.

     PLACED IN THE MODEL'S FRAME, NOT THE OTHER WAY ROUND. The record draws the
     model as exported and every pin and orbit point is in that frame, so the
     ground is turned to meet it: the Site view (src/map/scene3d.ts) turns the
     model by `90° − deckBearing` to stand it on the map, so the ground here is
     turned by the opposite, centred under the model, with the DEM's level at the
     structure on the model's lowest point — the same footing the Site view gives
     it. */
  let groundGroup: import('three').Group | null = null;
  let groundOf: Ground | null = null;
  let groundShown = false;
  let groundBusy = false;
  let groundToken = 0;

  const clearGround = (): void => {
    groundToken += 1;
    groundTheme = null;
    groundOf?.dispose();
    groundOf = null;
    groundGroup = null;
  };

  /** Stand the ground under whatever model is on screen now. */
  const placeGround = (): void => {
    if (!groundGroup || !structure) return;
    const box = visibleBounds(THREE, structure);
    if (!box) return;
    const mid = box.getCenter(new THREE.Vector3());
    const b = findBridge(bridgeId);
    groundGroup.position.set(mid.x, box.min.y, mid.z);
    groundGroup.rotation.y = -((90 - (b?.deckBearing ?? 0)) * Math.PI) / 180;
  };

  const groundRefusal = (): string | null => {
    const b = findBridge(bridgeId);
    return b?.lat == null || b.lng == null
      ? 'This structure has no position on record, so there is no ground to draw'
      : null;
  };

  const showGround = async (): Promise<void> => {
    const b = findBridge(bridgeId);
    if (!b || b.lat == null || b.lng == null || !structure) return;
    clearGround();
    const token = groundToken;
    const radius = isolateRadiusKm() * 1000;
    groundBusy = true;
    toolbar?.refresh();
    try {
      const built = await buildGround(THREE, { lat: b.lat, lng: b.lng }, radius, painted ?? theme);
      if (token !== groundToken || mine !== generation) {
        built.dispose();
        return;
      }
      groundOf = built;
      const group = built.group;
      groundGroup = group;
      scene.add(group);
      placeGround();
      groundTheme = built.setTheme;

      /* Room to stand back and see all of it, and a camera that goes there. Zoom
         All still frames the structure. */
      orbit.maxDistance = Math.max(orbit.maxDistance, radius * 6);
      lens.far = Math.max(lens.far, radius * 12);
      lens.updateProjectionMatrix();
      view = null;
      projection = 'perspective';
      clearPivot();
      /* A disc is flat, so the sphere that fits it is mostly empty air above and below:
         fitted at about half the radius, the ground fills the canvas from the usual angle. */
      frameModel(THREE, orbit, new THREE.Sphere(group.position.clone(), radius * 0.55), true);
    } catch {
      /* No ground is a view without ground, not a broken model. */
    } finally {
      if (token === groundToken) {
        groundBusy = false;
        toolbar?.refresh();
      }
    }
  };

  ground = {
    toggle: () => {
      groundShown = !groundShown;
      if (groundShown) void showGround();
      else {
        clearGround();
        groundBusy = false;
      }
      toolbar?.refresh();
    },
    shown: () => groundShown,
    busy: () => groundBusy,
    radiusKm: () => isolateRadiusKm(),
    setRadiusKm: (km) => {
      setIsolateRadiusKm(km as IsolateRadiusKm);
      if (groundShown) void showGround();
      toolbar?.refresh();
    },
    refusal: groundRefusal,
  };

  camera = {
    zoomIn: () => zoomBy(0.8),
    zoomOut: () => zoomBy(1.25),
    zoomAll,
    standardView: snapTo,
    activeView: () => view,
    projection: () => projection,
    setProjection: (mode) => {
      /* Leaving an axial view for perspective goes back to the three-quarter view,
         as Canvas3D.SetCameraMode does — a perspective camera still pointing
         straight down is nobody's idea of the bridge. */
      const leaving = mode === 'perspective' && view !== null;
      projection = mode;
      view = null;
      if (leaving) zoomAll();
      toolbar?.refresh();
    },
    armOrbitPoint: setArming,
    orbitPointArmed: () => arming,
    ready: () => structure !== null,
  };

  /* A drag moves the camera off whichever of the six it was sitting on, so the
     toolbar stops claiming it. `controlstart` fires for user input only. */
  const onControlStart = (): void => {
    if (view === null) return;
    view = null;
    toolbar?.refresh();
  };
  orbit.addEventListener('controlstart', onControlStart);

  /* SET ORBIT POINT is a toggle: while it is on, every press sets the pivot, in
     the capture phase so it runs before camera-controls' own pointerdown caches
     the old one — App's BindSetOrbitPointToggle. The press then carries on into
     camera-controls, so a drag from it orbits the new point. It stays on until the
     button is pressed again or Esc. */
  /* Set when the orbit point took this press, so the same click is not also read
     as a click on whatever pin was under it. */
  let pressTaken = false;
  const onArmedDown = (ev: PointerEvent): void => {
    if (!arming || ev.button !== 0 || !structure) return;
    pressTaken = true;
    const rect = renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((ev.clientX - rect.left) / rect.width) * 2 - 1,
      -((ev.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, active());
    const hit = ray.intersectObject(structure, true)[0];
    if (hit) {
      void orbit.setOrbitPoint(hit.point.x, hit.point.y, hit.point.z);
      showPivot(hit.point.x, hit.point.y, hit.point.z);
    }
  };
  renderer.domElement.addEventListener('pointerdown', onArmedDown, true);

  const rayAt = (x: number, y: number): import('three').Raycaster => {
    const rect = renderer.domElement.getBoundingClientRect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(
      new THREE.Vector2(
        ((x - rect.left) / rect.width) * 2 - 1,
        -((y - rect.top) / rect.height) * 2 + 1,
      ),
      active(),
    );
    return ray;
  };

  const pinAt = (x: number, y: number): RegisterDefect | null => {
    if (!pinGroup.children.length) return null;
    const hit = rayAt(x, y).intersectObjects(pinGroup.children, false)[0];
    return hit ? (pinIds.get(hit.object.uuid) ?? null) : null;
  };

  /* A CLICK, AS OPPOSED TO THE START OF AN ORBIT. camera-controls owns every drag;
     what is decided here is only whether the pointer came up where it went down.
     No pointer capture, so both read the same stream — see wireTap in
     src/inspector/scene.ts, of which this is the one-pointer subset. */
  let downAt: { id: number; x: number; y: number } | null = null;
  const onDown = (ev: PointerEvent): void => {
    const taken = pressTaken;
    pressTaken = false;
    downAt =
      ev.button === 0 && !taken ? { id: ev.pointerId, x: ev.clientX, y: ev.clientY } : null;
  };
  const onUp = (ev: PointerEvent): void => {
    const start = downAt;
    downAt = null;
    if (!start || start.id !== ev.pointerId || !structure) return;
    if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > CLICK_SLOP_PX) return;

    if (placing) {
      const hit = rayAt(ev.clientX, ev.clientY).intersectObject(structure, true)[0];
      /* A click past the bridge places nothing and stays armed: missing a thin
         bearing is the commonest way to get this wrong, and making somebody press
         the button again for it is a punishment for aiming. */
      if (!hit || !placingFor) return;
      /* The normal comes back in the hit object's own space; transformed, for the
         reason pickAt in src/inspector/scene.ts gives. */
      const n = hit.normal
        ? hit.normal
            .clone()
            .applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld))
            .normalize()
        : new THREE.Vector3(0, 1, 0);
      const component = qualifiedNameAt(hit.object, structure);
      const part = componentNodeAt(hit.object, structure);
      setPlacing(false);
      actions.openLogDefect(placingFor, {
        component,
        componentId: part ? (nodeId(part) ?? '') : '',
        pin: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
        normal: { x: n.x, y: n.y, z: n.z },
      });
      return;
    }

    /* A pin is a defect: on the desk, clicking one opens its details on the
       Defects page it is listed in. */
    const hit = !arming && pinOpensPanel ? pinAt(ev.clientX, ev.clientY) : null;
    if (hit) actions.showDefect(hit.id);
  };
  /* Hovering a pin names it — the one way to tell two orange dots apart without
     opening the panel. Pins only, and only with no button held, so an orbit costs
     no raycasts. */
  const onHover = (ev: PointerEvent): void => {
    if (ev.buttons !== 0) return;
    const d = placing || arming ? null : pinAt(ev.clientX, ev.clientY);
    renderer.domElement.title = d ? pinLabel(d) : '';
    renderer.domElement.classList.toggle('det-live__canvas--pin', d !== null && pinOpensPanel);
  };
  renderer.domElement.addEventListener('pointerdown', onDown);
  renderer.domElement.addEventListener('pointermove', onHover);
  window.addEventListener('pointerup', onUp);

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Escape') return;
    if (arming) setArming(false);
    if (placing) setPlacing(false);
  };
  window.addEventListener('keydown', onKey);

  /* NOT DRAWN WHILE IT IS NOT ON SCREEN. `track()` hides this canvas when the
     placeholder scrolls out of the panel, and until now the render loop carried on
     behind it — a full pass over a 155-part model, sixty times a second, at up to
     two device pixels, to produce frames nobody can see. That is the cost paid
     exactly when the main thread is busiest, which is while somebody is scrolling
     the record that hid it.

     The clock is read and discarded on the way back in, so camera-controls is handed
     the frame's own delta rather than the whole time spent off screen — a second-long
     delta would finish any easing in one step and jump the camera. */
  const setDrawing = (on: boolean): void => {
    if (on === drawing) return;
    drawing = on;
    if (on) {
      clock.getDelta();
      draw();
    } else {
      cancelAnimationFrame(raf);
    }
  };
  onVisible = setDrawing;

  dispose = () => {
    setDrawing(false);
    /* First, so the walk below disposes the model's own materials and not the
       playback's stand-ins. */
    closeSim();
    simulate = null;
    onSimInput = null;
    camera = null;
    place = null;
    onPins = null;
    /* The ground's lines are not meshes, so the walk below would miss them. */
    clearGround();
    ground = null;
    hidePivot();
    renderer.domElement.removeEventListener('pointerdown', onDown);
    renderer.domElement.removeEventListener('pointermove', onHover);
    window.removeEventListener('pointerup', onUp);
    orbit.removeEventListener('controlstart', onControlStart);
    renderer.domElement.removeEventListener('pointerdown', onArmedDown, true);
    window.removeEventListener('keydown', onKey);
    renderer.domElement.removeEventListener('wheel', onWheel);
    orbit.dispose();
    /* Walked and disposed by hand. A WebGL context is not garbage the browser
       reclaims on its own schedule — Chrome caps a tab at about sixteen and the
       Site view holds one of its own — and every geometry and material in a
       155-part model is a buffer on the GPU. */
    scene.traverse((node) => {
      const mesh = node as import('three').Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry?.dispose();
      const material = mesh.material as
        | import('three').Material
        | import('three').Material[]
        | undefined;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
    renderer.dispose();
    renderer.domElement.remove();
    structure = null;
  };

  /* Only if the placeholder is actually on screen — the geometry can perfectly well
     finish loading while somebody has scrolled past the slot, and `track()` is
     already running and has recorded which it is. */
  setDrawing(onScreen);

  /**
   * Put a loaded model on screen: framed, ready, and with its tree published.
   *
   * A function rather than a straight line through `build`, because it is now run
   * TWICE in the life of one mount — once for whatever the device had to draw with,
   * and again if the background freshness check came back with different bytes. See
   * the `onUpdated` handler below for why that swap is wanted here in particular.
   */
  const show = (root: import('three').Object3D): void => {
    structure = root;
    scene.add(root);

    /* FRAMED FROM THE MODEL'S OWN BOUNDS, not from any remembered camera. A bridge
       is 140 m along one axis and 12 along the others, so any fixed distance is
       either inside the deck or in the next valley.

       AND FRAMED THE WAY THE MODELLER FRAMES IT, which is what changed here. This
       used to establish its own direction — up, along and across in a
       0.55 : 0.36 : 0.62 ratio — and then solve the distance with a `fitToBox` padded
       by a metre. Both were defensible and neither was the vendored modeller's, so
       the same `.glb` opened at one size and angle on this record, another in the
       Defect Inspector, and a third in the application it was drawn in. ./framing.ts
       is now the single answer for all three; its header carries the account of the
       direction, the sphere and why there is no padding factor.

       The clamps and the planes stay here, because they are about what this panel
       lets somebody do rather than about where it opens — and they are set BEFORE
       the camera moves, or the outer clamp would pull the fitted distance back in
       and undo the framing. */
    drawPins(pins);

    const sphere = modelBounds(THREE, root);
    const reach = sphere ? sphere.radius * 2 : 50;
    orbit.minDistance = Math.max(0.1, reach / 500);
    orbit.maxDistance = reach * 10;
    lens.near = Math.max(0.1, reach / 800);
    lens.far = reach * 40;
    lens.updateProjectionMatrix();
    view = null;
    projection = 'perspective';
    clearPivot();
    if (sphere) frameModel(THREE, orbit, sphere, false);
    /* A newer export under ground that is already drawn: stand it under this one,
       and keep the room to see all of it. */
    if (groundGroup) {
      placeGround();
      const radius = isolateRadiusKm() * 1000;
      orbit.maxDistance = Math.max(orbit.maxDistance, radius * 6);
      lens.far = Math.max(lens.far, radius * 12);
      lens.updateProjectionMatrix();
    }
    toolbar?.refresh();

    actions.setHeroViewportReady(true);
    runPendingSim();
    /* THE TREE, from the file that is now on screen. This is what the framed
       modeller was still being kept alive for; see the header. */
    /* With each part's world extent, so the tree can say where on the structure
       a part is — see `groupByLocation` in src/data/componentTree.ts. */
    root.updateMatrixWorld(true);
    actions.setHeroComponentTree(
      componentTreeOf(root, (node) => new THREE.Box3().setFromObject(node as import('three').Object3D)),
    );
  };

  /**
   * Take one model back off the GPU.
   *
   * The mount's `dispose` above walks the whole scene and is the right thing at the
   * end; this is for the middle, when a newer export replaces what is drawn and the
   * old one has to go without taking the lights and the renderer with it.
   */
  const release = (root: import('three').Object3D): void => {
    scene.remove(root);
    root.traverse((node) => {
      const mesh = node as import('three').Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry?.dispose();
      const material = mesh.material as
        | import('three').Material
        | import('three').Material[]
        | undefined;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
  };

  /* THE VIEW THAT TAKES AN UPDATE LIVE, wired now that there is a scene to swap
     inside. The check behind the cached answer is one conditional request; where it
     finds a newer export this panel swaps to it, because it is the record's picture
     of the bridge and it has nothing anchored to the old triangles. The Defect
     Inspector deliberately does not — somebody is pinning findings onto what is on
     its screen — and the Site view rebuilds from the cache on its next mount.

     Assigned before the bytes are awaited, so a check that resolves early still
     finds a handler; `loading` above is what holds the callback until it does. */
  onFresh = (bytes) => {
    void (async () => {
      let updated: import('three').Object3D;
      try {
        const next = await parseGlb(new loaders.GLTFLoader(), bytes);
        updated = next.scene as import('three').Object3D;
      } catch {
        /* Bytes that will not parse are not worth taking the drawn bridge down
           for: the stored copy is already replaced, so the next mount reports the
           failure through the path below, and until then the record keeps showing
           the model it successfully loaded. */
        return;
      }
      /* Nothing drawn yet means the first load is still parsing and this check
         somehow beat it. Dropped rather than raced: two `show`s would put two
         bridges in the scene, and the newer bytes are already stored, so the next
         mount opens on them. */
      if (mine !== generation || !structure) return;
      closeSim();
      release(structure);
      show(updated);
    })();
  };

  try {
    /* FROM THE DEVICE FIRST. src/core/geometryCache.ts answers out of IndexedDB when
       this model has been drawn here before, which is most visits to a record an
       engineer is working on, and the panel fills in the frame it opens in instead
       of after a multi-megabyte download. The request itself went out at the top of
       this function, alongside the library — see there. */
    const geometry = await loading;
    if (mine !== generation) return; // unmounted while the file was in flight
    const gltf = await parseGlb(new loaders.GLTFLoader(), geometry.bytes);
    if (mine !== generation) return;
    show(gltf.scene as import('three').Object3D);
  } catch {
    /* Offline with nothing cached, or an object the bucket has lost. Said rather
       than papered over: the record's caption is the only thing that can tell an
       engineer whether they are looking at their bridge or at nothing. */
    if (mine !== generation) return;
    actions.setHeroViewportReady(false);
    actions.setHeroComponentTree(null);
    /* Reported through the same channel a failed re-export uses, which is what the
       record already draws a geometry error from — one place for "the 3D view has
       nothing to show and here is why", rather than a second one nothing renders. */
    actions.geometryBuildFinished(
      'The 3D geometry could not be loaded on this device. The model is on the server — ' +
        'check the connection and reload.',
    );
  }
}

/* --------------------------- lifecycle --------------------------- */

function destroy(): void {
  generation += 1;
  stopTracking?.();
  stopTracking = null;
  onResize = null;
  onTheme = null;
  /* Cleared with the rest, and `onScreen` put back to its optimistic default: the
     next mount's `track()` has not measured anything yet, and a slot left over from
     the last one as "scrolled away" would keep the new scene from drawing its first
     frame. */
  onVisible = null;
  onWheelMode = null;
  onScreen = true;
  painted = null;
  dispose?.();
  dispose = null;
  camera = null;
  place = null;
  onPins = null;
  simulate = null;
  simPending = false;
  onSimInput = null;
  ground = null;
  toolbar?.destroy();
  toolbar = null;
  host?.remove();
  host = null;
  showing = null;
  actions.setHeroViewportReady(false);
  actions.setHeroComponentTree(null);
  actions.resetComponentTreeExpanded();
}

/**
 * Reconcile the rendered model with state. Called from paint() on every render.
 *
 * Mounted on the bridge record, for a model that HAS GEOMETRY IN THE BUCKET, while
 * no import is running. That last condition is not about writers any more — nothing
 * here writes — it is that an import is about to replace the very file this would be
 * drawing, and the record shows the import's own progress in this slot meanwhile.
 *
 * WHAT IS NO LONGER A CONDITION is whether this device holds the mesh. There is no
 * device-local mesh in this path: the geometry is one object in R2, the same one for
 * everybody. `meshAvailability`, the restore replay and the whole apparatus that
 * existed to tell "the model displays a mesh" from "there is a mesh here to display"
 * are not consulted, because the question cannot arise.
 */
export function syncMeshView(s: Readonly<State>): void {
  const zooms = s.shell !== 'field';
  if (zooms !== wheelZooms) {
    wheelZooms = zooms;
    onWheelMode?.(zooms);
  }
  const model = heroModel(s.models.models);
  const importing = s.modelImport?.bridgeId === s.selectedId;
  const wanted =
    s.screen === 'detail' && !importing && model?.hasGeometry
      ? { bridgeId: s.selectedId, modelId: model.id, version: model.geometryVersion }
      : null;

  if (!wanted) {
    if (host) destroy();
    return;
  }

  /* WHAT THE PINS AND THE LOG DEFECT BUTTON READ, brought up to date on every
     paint — cheap, and applied to a live scene in place rather than by remounting. */
  placingFor = wanted.bridgeId;
  pinOpensPanel = s.shell !== 'field';
  /* DEFECTS ARE BRIDGE INSPECTION'S. The model is in every application, but
     its pins and its Log defect button are the condition record's, so the other
     three draw the structure bare — see `defectsShown`. */
  defectsShown = appForShell(s.shell).id === 'inspection';
  const nextPins = defectsShown ? pinSetFor(s, wanted.bridgeId) : NO_PINS;
  if (nextPins.defects !== pins.defects || !sameVec(nextPins.draft, pins.draft)) {
    pins = nextPins;
    onPins?.(pins);
  }
  const nextRefusal = !defectsShown
    ? 'Inspections are logged in Bridge Inspection'
    : can(s, 'defect.log')
      ? null
      : whyNot(s, 'Logging an inspection', 'defect.log');
  host?.classList.toggle('det-live--bare', !defectsShown);
  if (nextRefusal !== refusal) {
    refusal = nextRefusal;
    if (refusal !== null) place?.armPlace(false);
    toolbar?.refresh();
  }

  /* THE PLAYBACK IS CONSTRUCTION MONITORING'S AND CONSTRUCTION SCHEDULER'S.
     Monitoring plays the record that is open: `progress` is scoped to the
     selected structure, and only once it has been read. The Scheduler plays the
     accepted baseline — a proposed one is never the programme (LC-40). */
  const app = appForShell(s.shell).id;
  const nextShown = app === 'monitoring' || app === 'scheduler';
  const nextSim: SimInput =
    app === 'scheduler'
      ? programmeInputFor(s.baseline?.bridgeId === wanted.bridgeId ? s.baseline : null)
      : nextShown && s.progressRead === 'read'
        ? simInputFor(s, wanted.bridgeId)
        : NO_SIM;
  host?.classList.toggle('det-live--simulates', nextShown);
  if (nextShown !== simShown || nextSim !== simInput) {
    simShown = nextShown;
    simInput = nextSim;
    onSimInput?.(simInput);
    toolbar?.refresh();
    runPendingSim();
  }

  if (
    showing &&
    (showing.bridgeId !== wanted.bridgeId ||
      showing.modelId !== wanted.modelId ||
      showing.version !== wanted.version)
  ) {
    destroy();
  }
  if (host) {
    /* Already up on the right model, so the only thing that can have changed is how
       it is lit. Applied in place — a remount here would throw away the camera the
       user has just moved, to change one colour. */
    if (s.theme !== painted) onTheme?.(s.theme);
    return;
  }

  generation += 1;
  const mine = generation;
  host = document.createElement('div');
  host.className = [
    'det-live',
    'det-live--mesh',
    !defectsShown && 'det-live--bare',
    simShown && 'det-live--simulates',
  ]
    .filter(Boolean)
    .join(' ');
  document.body.appendChild(host);
  toolbar = mountViewToolbar(host, delegate, defectDelegate, simDelegate, terrainDelegate);
  showing = { bridgeId: wanted.bridgeId, modelId: wanted.modelId, version: wanted.version };
  actions.setHeroViewportReady(false);
  actions.setHeroComponentTree(null);
  /* A label path from one bridge's tree can coincidentally match another's, so a
     leftover expanded set would silently un-collapse nodes in a tree nobody has
     opened. Same reasoning as ./viewport.ts. */
  actions.resetComponentTreeExpanded();

  /* Tracked from the first frame, before the 600 kB of three.js has arrived, so the
     empty host is already over the placeholder rather than in the top-left corner
     of the window for a second. */
  track();

  void build(wanted.bridgeId, wanted.modelId, s.theme, mine, wanted.version).catch(() => {
    if (mine !== generation) return;
    actions.geometryBuildFinished('The 3D view could not start on this device.');
  });
}

/* --------------------------- the pins, from state --------------------------- */

/**
 * The structure's defects, as the pins read them — memoised on the register's own
 * array, so a paint that did not touch the defects hands the scene the same list
 * and nothing is rebuilt.
 */
let pinSource: { all: readonly RegisterDefect[]; bridgeId: string; mine: RegisterDefect[] } | null =
  null;

function pinSetFor(s: Readonly<State>, bridgeId: string): PinSet {
  if (!pinSource || pinSource.all !== s.registerDefects || pinSource.bridgeId !== bridgeId) {
    pinSource = {
      all: s.registerDefects,
      bridgeId,
      mine: s.registerDefects.filter((d) => d.bridgeId === bridgeId),
    };
  }
  /* The one being logged, while its dialog is open on this structure. */
  const draft = s.defectBridgeId === bridgeId ? s.defectDraft.pin : null;
  return { defects: pinSource.mine, draft };
}

/**
 * The structure's record, as the playback reads it — memoised on the store's own
 * arrays, so a paint that did not touch the record hands the playback the same
 * object and nothing is replanned.
 */
let simSource: { progress: readonly ProgressEntry[]; returns: readonly SiteReturn[]; input: SimInput } | null = null;

function simInputFor(s: Readonly<State>, bridgeId: string): SimInput {
  if (!simSource || simSource.progress !== s.progress || simSource.returns !== s.siteReturns) {
    simSource = {
      progress: s.progress,
      returns: s.siteReturns,
      input: {
        /* ACCEPTED ONLY, as the variance counts them (LC-52): a draft is somebody's
           claim, and the model does not show it as built before it is reviewed. */
        entries: playedEntries(s.progress.filter((e) => e.bridgeId === bridgeId)),
        returns: s.siteReturns.filter((r) => r.bridgeId === bridgeId),
      },
    };
  }
  return simSource.input;
}

/** The accepted programme, as the Scheduler's playback reads it — one object per baseline. */
let programmeSource: { baseline: Baseline | null; input: SimInput } | null = null;

function programmeInputFor(baseline: Baseline | null): SimInput {
  if (!programmeSource || programmeSource.baseline !== baseline) {
    programmeSource = { baseline, input: { entries: [], returns: [], programme: baseline } };
  }
  return programmeSource.input;
}

function sameVec(a: Vec3 | null, b: Vec3 | null): boolean {
  if (a === b) return true;
  return a !== null && b !== null && a.x === b.x && a.y === b.y && a.z === b.z;
}
